import { Component } from '@theme/component';

/**
 * @typedef {object} Refs
 * @property {HTMLButtonElement} addButton
 * @property {HTMLButtonElement} removeButton
 */

/**
 * Adds or removes the current product from the merchant wishlist (DigiSmoothie app).
 *
 * @extends {Component<Refs>}
 */
class WishlistHandlerComponent extends Component {
  requiredRefs = ['addButton', 'removeButton'];

  /** @type {boolean} */
  #busy = false;

  /** @type {AbortController | null} */
  #abortController = null;

  connectedCallback() {
    super.connectedCallback();
    this.#abortController = new AbortController();
    this.refs.addButton.addEventListener('click', this.#handleAdd);
    this.refs.removeButton.addEventListener('click', this.#handleRemove);
  }

  disconnectedCallback() {
    this.refs.addButton.removeEventListener('click', this.#handleAdd);
    this.refs.removeButton.removeEventListener('click', this.#handleRemove);
    this.#abortController?.abort();
    this.#abortController = null;
    super.disconnectedCallback();
  }

  /** @param {MouseEvent} event */
  #handleAdd = (event) => {
    event.preventDefault();
    if (!this.dataset.customerId) {
      window.location.href = '/account/login';
      return;
    }
    void this.#updateWishlist('add');
  };

  /** @param {MouseEvent} event */
  #handleRemove = (event) => {
    event.preventDefault();
    void this.#updateWishlist('remove');
  };

  /** @param {'add' | 'remove'} action */
  async #updateWishlist(action) {
    if (this.#busy) return;

    const appUrl = window.digismoothieWishlistApp?.appUrl;
    if (!appUrl) {
      console.error('appUrl is not available');
      return;
    }

    this.#busy = true;
    try {
      const response = await fetch(appUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          shop_url: window.Shopify.shop,
          customer_id: this.dataset.customerId,
          product_reference: this.dataset.productReference,
          action,
        }),
        signal: this.#abortController?.signal,
      });

      const data = /** @type {{ products_count?: number }} */ (await response.json());
      const count = data.products_count ?? 0;

      if (action === 'add') {
        this.refs.addButton.classList.add('hidden');
        this.refs.removeButton.classList.remove('hidden');
        this.refs.removeButton.classList.add('in-wishlist');
        this.refs.removeButton.addEventListener('animationend', this.#clearInWishlistClass, { once: true });
      } else {
        this.refs.removeButton.classList.add('hidden');
        this.refs.addButton.classList.remove('hidden');
      }

      if (window.location.pathname === '/pages/wishlist') {
        this.closest('.wishlist-item')?.remove();
        this.#syncWishlistPageEmptyState(count);
      }

      const productsCountEl = document.getElementById('wishlist-products-count');
      if (productsCountEl) {
        productsCountEl.textContent = String(count);
      }

      const countBubble = document.querySelector('.wishlist-count-bubble');
      if (countBubble) {
        countBubble.classList.toggle('hidden', count === 0);
      }
    } catch (error) {
      if (error instanceof Error && error.name === 'AbortError') return;
      console.error('[wishlist-handler]', error);
    } finally {
      this.#busy = false;
    }
  }

  #clearInWishlistClass = () => {
    this.refs.removeButton.classList.remove('in-wishlist');
  };

  /** @param {number} productsCount */
  #syncWishlistPageEmptyState(productsCount) {
    const emptyWishlistMessage = document.querySelector('.empty-wishlist');
    if (!emptyWishlistMessage) return;
    emptyWishlistMessage.classList.toggle('hidden', productsCount !== 0);
  }
}

if (!customElements.get('wishlist-handler')) {
  customElements.define('wishlist-handler', WishlistHandlerComponent);
}
